import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, firstValueFrom, forkJoin, of } from 'rxjs';
import { ODataServiceFactory } from 'angular-odata';
import { Select } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { Contact, ProductionSite } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { ToastService } from '../../../shared/services/toast.service';

interface DocumentToken {
  id: string;
  token: string;
  documentType?: string;
  documentId?: string;
  recipientEmail?: string;
  emailSent: boolean;
  createdOn: string;
  expiresOn: string;
  lastDownloadedOn?: string;
  downloadCount: number;
  isRevoked: boolean;
  createdBy?: string;
}

/**
 * Certificate Tokens Component
 *
 * Manages secure download tokens for certificate documents. This component allows sharing
 * certificates with contacts via email-secured download links with expiration dates.
 * Used for coater licenses (entity type `License`) and approved systems (`CertifiableSystem`);
 * the host passes the entity, the share endpoint and the organisation whose contacts can be picked.
 *
 * **Contact Loading Strategy:**
 * Contacts are loaded for `organisationId`. When the host only knows a `productionSiteId`
 * (license data may not have the production site expanded in all contexts), the production
 * site is fetched to resolve its customer organisation.
 *
 * **Token Management:**
 * - Tokens are created with configurable expiration periods (1-30 days)
 * - Download counts are tracked for audit purposes
 * - Tokens can be revoked immediately for security
 * - Full email addresses displayed for verification (no masking)
 *
 * **Modern Angular Patterns:**
 * - Uses signal-based inputs/outputs for better change detection
 * - OnPush change detection strategy for performance
 * - Standalone component with explicit imports
 */
@Component({
  selector: 'app-certificate-tokens',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    TableModule,
    TagModule,
    TooltipModule,
    ProgressSpinnerModule,
    DividerModule,
    Select,
    MultiSelectModule,
  ],
  template: `
    <div>
      <!-- Header with Share Button -->
      <div class="flex justify-content-between align-items-center mb-3">
        <h5 class="m-0">
          <i class="pi pi-key mr-2"></i>
          Download Tokens
        </h5>
        <p-button
          label="Share Certificate"
          icon="pi pi-envelope"
          (onClick)="openShareDialog()"
          severity="secondary"
          size="small"
          [outlined]="true"
          pTooltip="Share certificate with contacts"
          tooltipPosition="left">
        </p-button>
      </div>

      <!-- Tokens Table -->
      @if (tokens().length > 0) {
        <p-table [value]="tokens()" [scrollable]="true" scrollHeight="300px" styleClass="p-datatable-sm">
          <ng-template #header>
            <tr>
              <th>Recipient</th>
              <th>Sent</th>
              <th>Downloads</th>
              <th>Expires</th>
              <th>Status</th>
              <th style="width: 100px; text-align: center">Actions</th>
            </tr>
          </ng-template>
          <ng-template #body let-token>
            <tr>
              <td>
                <span [pTooltip]="token.recipientEmail">
                  {{ token.recipientEmail }}
                </span>
              </td>
              <td>
                {{ formatDate(token.createdOn) }}
                @if (token.emailSent) {
                  <i class="pi pi-check-circle text-green-500 ml-1" pTooltip="Email sent successfully"></i>
                }
              </td>
              <td>
                <span [class.text-orange-500]="token.downloadCount > 0">
                  {{ token.downloadCount || 0 }}
                  @if (token.maxDownloads) {
                    / {{ token.maxDownloads }}
                  }
                </span>
                @if (token.lastDownloadedOn) {
                  <br />
                  <small class="text-500"> Last: {{ formatDate(token.lastDownloadedOn) }} </small>
                }
              </td>
              <td>{{ formatDate(token.expiresOn) }}</td>
              <td>
                <p-tag
                  [value]="getTokenStatus(token).label"
                  [severity]="getTokenStatus(token).severity"
                  [rounded]="true">
                </p-tag>
              </td>
              <td>
                <div class="flex gap-1 justify-content-center">
                  @if (!token.isRevoked && !isTokenExpired(token)) {
                    <p-button
                      icon="pi pi-link"
                      (onClick)="copyLink(token)"
                      severity="secondary"
                      styleClass="p-button-sm p-button-text"
                      pTooltip="Copy download link">
                    </p-button>
                    <p-button
                      icon="pi pi-ban"
                      (onClick)="revokeToken(token)"
                      severity="danger"
                      styleClass="p-button-sm p-button-text"
                      pTooltip="Revoke token">
                    </p-button>
                  }
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="6" class="text-center text-500">No download tokens have been created yet</td>
            </tr>
          </ng-template>
        </p-table>
      } @else {
        <div class="text-center p-3 surface-100 border-round">
          <i class="pi pi-inbox text-3xl text-400 mb-2"></i>
          <p class="text-500 m-0">No tokens created yet</p>
          <p class="text-sm text-400 m-0">Tokens will appear here when the certificate is shared</p>
        </div>
      }

      <!-- Share Dialog -->
      <p-dialog
        header="Share Certificate"
        [(visible)]="showShareDialog"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [style]="{ width: '520px' }"
        [dismissableMask]="false"
        [baseZIndex]="2000"
        [closeOnEscape]="true"
        styleClass="share-certificate-dialog"
        (onHide)="onDialogHide()">
        <div class="flex flex-column gap-3 pb-3">
          <!-- Contact Selection -->
          <div class="field mb-2">
            <label for="contact-select" class="block mb-2">Select Contacts</label>
            @if (contactsLoading()) {
              <div class="flex align-items-center justify-content-center p-3 surface-100 border-round">
                <p-progressspinner
                  [style]="{ width: '30px', height: '30px' }"
                  styleClass="w-2rem h-2rem"
                  strokeWidth="4">
                </p-progressspinner>
                <span class="ml-2 text-500">Loading contacts...</span>
              </div>
            } @else if (contactsLoadError()) {
              <div class="p-2 surface-100 border-round text-500 text-sm">
                <i class="pi pi-exclamation-triangle mr-2 text-orange-500"></i>
                Failed to load contacts: {{ contactsLoadError() }}
              </div>
            } @else if (organizationContacts().length > 0) {
              <p-multiselect
                id="contact-select"
                [options]="organizationContacts()"
                [(ngModel)]="selectedContacts"
                optionLabel="displayName"
                optionDisabled="optionDisabled"
                placeholder="Choose contacts"
                display="chip"
                [showClear]="true"
                [filter]="true"
                styleClass="w-full"
                [appendTo]="'body'"
                [overlayOptions]="{ autoZIndex: true, baseZIndex: 3000 }"
                (onChange)="onContactSelected()">
                <ng-template let-contact #item>
                  <div class="flex flex-column">
                    <span>{{ contact.displayName }}</span>
                    <small class="text-500">{{ contact.email || 'No email address' }}</small>
                  </div>
                </ng-template>
              </p-multiselect>
              <small class="text-500 block mt-1">Contacts tagged as certificate recipients are pre-selected.</small>
            } @else {
              <div class="p-2 surface-100 border-round text-500 text-sm">
                No contacts available for this organization
              </div>
            }
          </div>

          <div class="flex align-items-center gap-2 my-2">
            <div class="flex-1 border-top-1 surface-border"></div>
            <span class="text-sm text-500">OR</span>
            <div class="flex-1 border-top-1 surface-border"></div>
          </div>

          <!-- Custom Email -->
          <div class="field mb-2">
            <label for="custom-email" class="block mb-2">Custom Email</label>
            <input
              id="custom-email"
              type="email"
              pInputText
              [(ngModel)]="customEmail"
              placeholder="Enter email address"
              class="w-full"
              (input)="onCustomEmailInput()" />
            @if (customEmail && !isValidCustomEmail()) {
              <small class="text-red-500 mt-1 block"> Please enter a valid email address </small>
            }
          </div>

          @if (customEmail) {
            <div class="field mb-2">
              <label for="custom-name" class="block mb-2">Recipient Name</label>
              <input
                id="custom-name"
                type="text"
                pInputText
                [(ngModel)]="customName"
                placeholder="Enter recipient name"
                class="w-full" />
            </div>
          }

          <!-- Validity Period -->
          <div class="field mb-3">
            <label for="validity-select" class="block mb-2">Valid for</label>
            <p-select
              id="validity-select"
              [(ngModel)]="expiryDays"
              [options]="expiryOptions"
              optionLabel="label"
              optionValue="value"
              styleClass="w-full"
              [appendTo]="'body'"
              [overlayOptions]="{ autoZIndex: true, baseZIndex: 3000 }">
            </p-select>
          </div>
        </div>

        <ng-template #footer>
          <div class="flex justify-content-between gap-2">
            <p-button label="Cancel" (onClick)="cancelShare()" severity="secondary" [outlined]="true"> </p-button>
            <div class="flex gap-2">
              <p-button
                label="Create Link"
                icon="pi pi-link"
                (onClick)="createDownloadLink()"
                severity="secondary"
                [outlined]="true"
                [loading]="loading()"
                [disabled]="loading()">
              </p-button>
              <p-button
                label="Send via Email"
                icon="pi pi-send"
                (onClick)="createAndSendToken()"
                [loading]="loading()"
                [disabled]="!canSend() || loading()">
              </p-button>
            </div>
          </div>
        </ng-template>
      </p-dialog>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host ::ng-deep p-table .p-datatable .p-datatable-thead > tr > th {
        padding: 0.375rem 0.5rem !important;
        font-size: 0.8125rem !important;
      }

      :host ::ng-deep p-table .p-datatable .p-datatable-tbody > tr > td {
        padding: 0.375rem 0.5rem !important;
        font-size: 0.8125rem !important;
      }

      :host ::ng-deep p-table .p-datatable .p-datatable-tbody > tr > td small {
        font-size: 0.75rem !important;
      }
      :host ::ng-deep .p-button-sm {
        padding: 0.25rem 0.5rem !important;
        font-size: 0.8125rem !important;
      }

      :host ::ng-deep .p-button-sm .p-button-icon {
        font-size: 0.8125rem !important;
      }

      :host ::ng-deep .p-tag {
        font-size: 0.6875rem;
        padding: 0.125rem 0.375rem;
      }
    `,
  ],
})
export class CertificateTokensComponent implements OnInit, OnDestroy {
  // Inputs using modern signal-based API
  /** WorkflowDocument entity type the certificate belongs to ('License' or 'CertifiableSystem'). */
  entityType = input.required<string>();
  entityId = input.required<string>();
  /** POST endpoint creating one share token per recipient (e.g. `/api/licenses/{id}/share-certificate`). */
  shareUrl = input.required<string>();
  /** Organisation whose contacts are offered as recipients. */
  organisationId = input<string | null | undefined>(undefined);
  /** Fallback when the organisation is not known: its customer is resolved from the production site. */
  productionSiteId = input<string | null | undefined>(undefined);
  /** The certificate document this panel belongs to; shared and listed tokens are bound to it. */
  documentId = input.required<string>();

  // Output using modern signal-based API
  tokensUpdated = output<void>();

  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  private odataFactory = inject(ODataServiceFactory);
  private cdr = inject(ChangeDetectorRef);

  loading = signal(false);
  tokens = signal<DocumentToken[]>([]);
  showShareDialog = false;

  // Contact loading states
  contactsLoading = signal(false);
  contactsLoadError = signal<string | null>(null);

  // Contact selection
  organizationContacts = signal<Contact[]>([]);
  selectedContacts: Contact[] = [];
  private preselectPending = false;
  customEmail: string = '';
  customName: string = '';

  expiryDays: number = 7;

  expiryOptions = [
    { label: '24 hours', value: 1 },
    { label: '7 days', value: 7 },
    { label: '14 days', value: 14 },
    { label: '30 days', value: 30 },
  ];

  activeTokensCount = signal(0);

  ngOnInit(): void {
    this.loadTokens();
    this.loadOrganizationContacts();
  }

  ngOnDestroy(): void {
    // Ensure dialog is properly closed and cleaned up
    this.cleanupDialog();
  }

  private cleanupDialog(): void {
    if (this.showShareDialog) {
      this.showShareDialog = false;
      // Reset all form fields
      this.selectedContacts = [];
      this.customEmail = '';
      this.customName = '';
      this.expiryDays = 7;
      // Force immediate change detection
      this.cdr.detectChanges();
    }
  }

  onDialogHide(): void {
    // Clean up when dialog is hidden by any means (X button, ESC key, etc.)
    this.cleanupDialog();
  }

  openShareDialog(): void {
    // Reset form before opening; contacts tagged as certificate recipients are pre-selected.
    // If the contacts are still loading, the pre-selection is applied once the load finishes.
    this.selectedContacts = this.defaultRecipientSelection();
    this.preselectPending = this.contactsLoading();
    this.customEmail = '';
    this.customName = '';
    this.expiryDays = 7;
    this.showShareDialog = true;
    // Ensure dialog is properly rendered
    this.cdr.detectChanges();
  }

  private defaultRecipientSelection(): Contact[] {
    return this.organizationContacts().filter((c) => !!c.email && (c.tags || []).includes('org:CertificateRecipient'));
  }

  async loadOrganizationContacts(): Promise<void> {
    this.contactsLoading.set(true);
    this.contactsLoadError.set(null);

    try {
      let customerId: string | undefined = this.organisationId() ?? undefined;
      const productionSiteId = this.productionSiteId();

      if (!customerId && productionSiteId) {
        // If we only have the productionSiteId, fetch the production site to get customerId
        console.log('Fetching production site with ID:', productionSiteId);
        try {
          const productionSiteService = this.odataFactory.entitySet<ProductionSite>('ProductionSites');
          const entityService = productionSiteService.entity(productionSiteId);
          const response = await firstValueFrom(entityService.fetch());

          console.log('Production site response:', response);

          if (response && typeof response === 'object' && 'customerId' in response) {
            customerId = response.entity?.customerId;
            console.log('Found customerId from production site:', customerId);
          }
        } catch (error) {
          console.error('Failed to fetch production site:', error);
          const httpError = error as { message?: string };
          const errorMsg = httpError?.message || 'Could not determine organization';
          this.contactsLoadError.set(errorMsg);
          return;
        }
      }

      if (!customerId) {
        console.log('No customerId found - no organization associated');
        // No error - just no organization associated
        this.organizationContacts.set([]);
        return;
      }

      console.log('Fetching contacts for customerId:', customerId);
      const contactsService = this.odataFactory.entitySet<Contact>('Contacts');
      const response = await firstValueFrom(
        contactsService
          .entities()
          .query((q) => {
            // Use the correct field name: OrganisationId (capital O, with 's')
            // GUID comparison doesn't use quotes in OData
            q.filter(`OrganisationId eq ${customerId}`);
            q.orderBy('firstName asc, lastName asc');
          })
          .fetch()
      );

      console.log('Contacts response:', response);

      // The OData response contains entities in the 'entities' property from angular-odata
      // But check for both patterns to be safe
      const contacts = response.entities || (response as { value?: Contact[] }).value || [];

      if (contacts && contacts.length > 0) {
        // The displayName already comes from the server, but ensure all contacts have it
        const contactsWithDisplayName = contacts.map((contact: Contact) => {
          // Use the displayName from server if available, otherwise construct it
          const displayName =
            contact.displayName ||
            `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
            contact.email ||
            'Unknown';

          return {
            ...contact,
            displayName: displayName,
            optionDisabled: !contact.email,
          };
        });

        console.log('Found contacts:', contactsWithDisplayName.length);
        console.log('Contacts with displayName:', contactsWithDisplayName);
        this.organizationContacts.set(contactsWithDisplayName);

        // Apply the deferred pre-selection when the dialog was opened before contacts loaded
        if (this.preselectPending && this.showShareDialog) {
          this.selectedContacts = this.defaultRecipientSelection();
        }

        // Force change detection to ensure dropdown updates
        this.cdr.detectChanges();
      } else {
        console.log('No contacts found for this organization');
        this.organizationContacts.set([]);
      }
    } catch (error) {
      console.error('Failed to load organization contacts - Full error:', error);
      const httpError = error as { message?: string; statusText?: string };
      const errorMsg = httpError?.message || httpError?.statusText || 'Unable to load contacts';
      this.contactsLoadError.set(errorMsg);
    } finally {
      this.preselectPending = false;
      this.contactsLoading.set(false);
    }
  }

  loadTokens(): void {
    this.loading.set(true);

    // Load tokens for this entity using OData endpoint
    const url = `/odata/DocumentDownloadTokens/GetByEntity(entityType='${this.entityType()}',entityId=${this.entityId()})`;

    this.http
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- missing proper typing for CUD operations
      .get<any>(url)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          const tokens = response.value || [];
          // Filter for certificate tokens only (not confirmation letters or other types)
          const certificateTokens = tokens.filter(
            (t: DocumentToken) => t.documentType === 'Certificate' && t.documentId === this.documentId()
          );
          this.tokens.set(certificateTokens);
          this.updateActiveTokensCount();
        },
        error: (error) => {
          console.error('Failed to load tokens:', error);
          // Initialize empty tokens array on error
          this.tokens.set([]);
          this.updateActiveTokensCount();
        },
      });
  }

  // Contact selection handlers
  onContactSelected(): void {
    if (this.selectedContacts.length > 0) {
      // Clear custom email when contacts are selected
      this.customEmail = '';
      this.customName = '';
    }
  }

  onCustomEmailInput(): void {
    if (this.customEmail) {
      // Clear selected contacts when custom email is entered
      this.selectedContacts = [];
    }
  }

  canSend(): boolean {
    return !!(this.selectedContacts.length > 0 || (this.customEmail && this.isValidCustomEmail()));
  }

  isValidCustomEmail(): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(this.customEmail);
  }

  createAndSendToken(): void {
    if (!this.canSend()) {
      return;
    }
    this.shareNewToken(false);
  }

  shareNewToken(createOnly: boolean = false): void {
    // Determine recipients from either selected contacts or custom input
    const recipients =
      this.selectedContacts.length > 0
        ? this.selectedContacts
            .filter((contact) => !!contact.email)
            .map((contact) => ({
              email: contact.email as string,
              name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
            }))
        : [{ email: this.customEmail, name: this.customName }];

    if (recipients.length === 0) {
      return;
    }

    this.loading.set(true);

    // Create one share token per recipient via the host-provided endpoint
    const requests = recipients.map((recipient) =>
      this.http
        .post<DocumentToken>(this.shareUrl(), {
          email: recipient.email,
          recipientName: recipient.name || undefined,
          expiryDays: this.expiryDays,
          sendEmail: !createOnly,
          documentId: this.documentId(),
        })
        .pipe(
          catchError((error) => {
            console.error('Failed to create token:', error);
            return of(null);
          })
        )
    );

    forkJoin(requests)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((results) => {
        // results map positionally to recipients
        const failedRecipients = recipients.filter((_, index) => results[index] === null);
        const successCount = results.length - failedRecipients.length;

        if (failedRecipients.length === 0) {
          const recipientInfo = recipients.length === 1 ? recipients[0].email : `${successCount} recipients`;
          this.toastService.displaySuccessMessage(
            createOnly ? `Download link created for ${recipientInfo}` : `Certificate link sent to ${recipientInfo}`
          );

          // Reset form and close dialog
          this.selectedContacts = [];
          this.customEmail = '';
          this.customName = '';
          this.expiryDays = 7;
          this.showShareDialog = false;

          // Force change detection to ensure proper cleanup
          this.cdr.detectChanges();

          // Reload tokens to get complete data from server
          this.loadTokens();
          this.tokensUpdated.emit();
        } else {
          // Keep the dialog open with the failed recipients still selected so they can be retried
          const failedEmails = failedRecipients.map((recipient) => recipient.email);
          if (this.selectedContacts.length > 0) {
            const failedEmailSet = new Set(failedEmails);
            this.selectedContacts = this.selectedContacts.filter((c) => c.email && failedEmailSet.has(c.email));
          }
          this.toastService.displayErrorMessage(
            successCount > 0
              ? `${createOnly ? 'Created links for' : 'Sent to'} ${successCount} recipient(s), but failed for: ${failedEmails.join(', ')}`
              : `${createOnly ? 'Failed to create link(s) for' : 'Failed to send to'}: ${failedEmails.join(', ')}`
          );
          this.cdr.detectChanges();

          if (successCount > 0) {
            this.loadTokens();
            this.tokensUpdated.emit();
          }
        }
      });
  }

  createDownloadLink(): void {
    this.shareNewToken(true);
  }

  cancelShare(): void {
    // Use centralized cleanup
    this.cleanupDialog();
  }

  revokeToken(token: DocumentToken): void {
    if (token.isRevoked) {
      return;
    }

    this.loading.set(true);

    // Use centralized document tokens API to revoke token
    this.http
      .post(`/api/documents/tokens/${token.id}/revoke`, { reason: 'Revoked by user' })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.tokens.update((tokens) => tokens.map((t) => (t.id === token.id ? { ...t, isRevoked: true } : t)));
          this.updateActiveTokensCount();

          this.toastService.displaySuccessMessage('The download link has been revoked');

          this.tokensUpdated.emit();
        },
        error: (error) => {
          console.error('Failed to revoke token:', error);
          this.toastService.displayErrorMessage('Failed to revoke token');
        },
      });
  }

  copyLink(token: DocumentToken): void {
    // Use public document download page
    const link = `${window.location.origin}/public/document/${token.token}`;

    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.toastService.displaySuccessMessage('Download link copied to clipboard');
      })
      .catch(() => {
        this.toastService.displayErrorMessage('Failed to copy link');
      });
  }

  isTokenExpired(token: DocumentToken): boolean {
    return new Date(token.expiresOn) < new Date();
  }

  updateActiveTokensCount(): void {
    const now = new Date();
    const activeCount = this.tokens().filter((t) => !t.isRevoked && new Date(t.expiresOn) > now).length;
    this.activeTokensCount.set(activeCount);
  }

  formatDate(date: string | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  }

  formatRelativeDate(date: string): string {
    const expiry = new Date(date);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'expired';
    } else if (diffDays === 0) {
      return 'today';
    } else if (diffDays === 1) {
      return 'tomorrow';
    } else {
      return `in ${diffDays} days`;
    }
  }

  getTokenStatus(token: DocumentToken): {
    label: string;
    severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary';
  } {
    if (token.isRevoked) {
      return { label: 'Revoked', severity: 'danger' };
    }
    if (this.isTokenExpired(token)) {
      return { label: 'Expired', severity: 'secondary' };
    }
    if (token.downloadCount > 0) {
      return { label: 'Downloaded', severity: 'warn' };
    }
    return { label: 'Active', severity: 'success' };
  }
}
